/**
 * CAPSTONE LAB — CodeAI101 chapter 7, the smallest honest v0.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 7, the last of them.
 *
 * ── A CHECKLIST YOU CANNOT CHEAT ──────────────────────────────────────────────────────────────
 * §6 defines a v0 as "the smallest possible system that has all seven of Section 2 — even if
 * narrowly", and defers agents, multi-tool, multi-agent and fine-tuning by name. So ticking an
 * ambitious extra does not improve the verdict, it changes it: the lab reports "roadmap shipped
 * early", by his definition rather than my opinion. That is the whole instrument, because the
 * failure this chapter addresses is not ignorance — it is temptation. "Every team is tempted."
 *
 * ── AND IT TIES BACK, BECAUSE THE SYNTHESIS IS REAL ───────────────────────────────────────────
 * Four of the seven and all five failure families name a Day, and a lab was built for every one of
 * those days before this chapter was reached. The learner has watched each of these fail with their
 * own hands, so this page names them and points back rather than re-explaining.
 *
 * ── IT DOES NOT SCORE THE LEARNER'S PROJECT ───────────────────────────────────────────────────
 * The verdict is about the CONFIGURATION, not the person or the idea. There is no mark and no
 * percentage: §6 gives a definition, and a definition applied is a fact, while a grade would be a
 * judgement the module never makes.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import Anchor from './anchor.jsx';
import {
  SEVEN, V0, ROADMAP, FAMILIES, judge, BOUNDED_IN_V0,
  SMALLEST, TEMPTED, SURVIVES, EARNS_ITS_PLACE, NOT_CLEVER_PROMPTS, UNDERNEATH, THE_QUESTION,
  SOURCE_2, SOURCE_5, SOURCE_6,
} from './capstone.mjs';

export default function CapstoneLab() {
  const [has, setHas] = useState({});
  const [extras, setExtras] = useState([]);
  const r = judge(has, extras);

  const toggle = (id) => setHas((h) => ({ ...h, [id]: !h[id] }));
  const toggleExtra = (id) => setExtras((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Capstone lab · v0 or roadmap</div>
      <h2 className="g-h2 lab-h">The smallest thing that survives an auditor</h2>
      <Anchor chapter={7} />
      <p className="g-body"><Value of={cited(SMALLEST, SOURCE_6)} /></p>

      {/* ── THE SEVEN ── */}
      <div className="lab-task">
        <div className="g-label">Tick what your system actually has</div>
        <div className="cap-seven">
          {SEVEN.map((s) => (
            <label key={s.id} className={`cap-item ${has[s.id] ? 'cap-on' : ''}`}>
              <input type="checkbox" checked={Boolean(has[s.id])} onChange={() => toggle(s.id)} />
              <div>
                <b>{s.n}. {s.name}</b>
                {s.chapter && <span className="cap-day">watched in chapter {s.chapter}</span>}
                <p className="g-body g-dim cap-q"><Value of={cited(s.quote, SOURCE_2)} /></p>
                {s.id === 'bounded' && has[s.id] && (
                  <p className="g-body cap-note">{BOUNDED_IN_V0}</p>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* ── THE TEMPTATION ── */}
      <div className="lab-task">
        <div className="g-label">And what you were tempted to add</div>
        <div className="lab-task-head">
          {ROADMAP.map((x) => (
            <button key={x.id} className={`g-btn ${extras.includes(x.id) ? 'cap-tempt' : ''}`}
              onClick={() => toggleExtra(x.id)}>{x.name}</button>
          ))}
        </div>
        <p className="g-body g-dim"><Value of={cited(EARNS_ITS_PLACE, SOURCE_6)} /></p>
      </div>

      {/* ── THE VERDICT ── */}
      <div className="lab-task">
        <div className="g-label">What you have</div>
        <div className={`cap-verdict ${r.isV0 ? 'cap-good' : ''}`}>
          {r.isV0 ? <Value of={computed(r.verdict)} /> : <Value of={refused(r.verdict)} />}
        </div>
        {r.missing.length > 0 && (
          <>
            <p className="g-body">Still missing, and each is one of the seven:</p>
            <ul className="fai-list">{r.missing.map((m) => <li key={m.id}>{m.n}. {m.name}</li>)}</ul>
          </>
        )}
        {r.premature.length > 0 && (
          <p className="g-body cap-premature">
            <b>{r.premature.map((p) => p.name).join(', ')}</b> {r.premature.length > 1 ? 'are' : 'is'} roadmap —
            deferred by name in §6, not because it is bad but because v0 does not need it to be honest.
          </p>
        )}
        {r.isV0 && <p className="g-body"><Value of={cited(SURVIVES, SOURCE_6)} /></p>}
        <p className="g-body cap-tempted"><Value of={cited(TEMPTED, SOURCE_6)} /></p>
      </div>

      {/* ── WHAT V0 LOOKS LIKE, IN HIS WORDS ── */}
      <details className="cou-tr">
        <summary className="g-label">What each of the seven looks like at its narrowest</summary>
        <ul className="fai-list">
          {V0.map((v) => <li key={v}><Value of={cited(v, SOURCE_6)} /></li>)}
        </ul>
        <p className="g-body g-dim"><Value of={cited(NOT_CLEVER_PROMPTS, SOURCE_2)} /></p>
      </details>

      {/* ── §5 THE SYNTHESIS ── */}
      <div className="lab-task">
        <div className="g-label">And when it goes wrong — which family?</div>
        <p className="g-body"><Value of={cited(THE_QUESTION, SOURCE_5)} /></p>
        {FAMILIES.map((f) => (
          <div key={f.id} className="cap-fam">
            <div className="cap-fhead"><b>{f.name}</b><span className="cap-day">chapter {f.chapter}</span></div>
            <p className="g-body g-dim"><Value of={cited(f.quote, SOURCE_5)} /></p>
          </div>
        ))}
        <p className="g-body cap-under"><Value of={cited(UNDERNEATH, SOURCE_5)} /></p>
      </div>

      <p className="g-body g-dim lab-why">
        Nothing here scores you. The verdict is about the configuration you ticked, and it is his
        definition applied rather than my opinion — §6 says a v0 is all seven and defers four things
        by name, so a system with agents is past v0 however good it is. That is a fact about the
        definition, not a mark.
        <br />
        One reading of the text is the lab's own and is marked where it appears: §2 requires bounded
        agent loops while §6's v0 requires zero agents. His qualifier — “even if narrowly” —
        resolves it, and the narrowest satisfaction of a bounded loop is no loop at all.
      </p>
    </div>
  );
}
